'use client';

import { motion, useInView, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { MIN_PER_CHAR_MS, TYPING_DURATION_MS } from './hero.constants';

// 뷰포트 진입(active) 후 startDelayMs 만큼 기다렸다가 0 → total 까지 글자 수를 순차로 센다.
// instant(reduced-motion)면 즉시 total. 반환값 = 지금까지 노출할 누적 글자 수.
function useTypewriter(
  total: number,
  {
    active,
    instant,
    startDelayMs,
  }: { active: boolean; instant: boolean; startDelayMs: number },
) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (instant) {
      setCount(total);
      return;
    }
    const perCharMs = Math.max(
      MIN_PER_CHAR_MS,
      Math.round(TYPING_DURATION_MS / Math.max(total, 1)),
    );
    let typed = 0;
    let intervalId: number | undefined;
    // 제목 컨테이너 진입 애니메이션이 끝난 뒤 타이핑을 시작하도록 지연
    const startId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        typed += 1;
        setCount(typed);
        if (typed >= total) window.clearInterval(intervalId);
      }, perCharMs);
    }, startDelayMs);
    return () => {
      window.clearTimeout(startId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [active, instant, total, startDelayMs]);

  return count;
}

// 각 줄의 시작 글자 오프셋(누적). 전역 count 를 줄별 진행도로 환산하는 데 쓴다.
// 예) ['ab','cde'] → [0, 2]
function cumulativeOffsets(lines: string[]): number[] {
  let acc = 0;
  return lines.map(line => {
    const start = acc;
    acc += line.length;
    return start;
  });
}

// 깜빡이는 타이핑 커서(장식)
function Caret() {
  return (
    <motion.span
      className="ml-[2px] inline-block h-[1em] w-[3px] translate-y-[-2px] bg-[#040435] align-middle"
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
    />
  );
}

// 한 줄. 레이아웃 시프트 방지 + 접근성/SEO 를 위해 '전체 텍스트'를 투명하게 항상 깔고(치수·스크린리더용),
// 그 위에 지금까지 타이핑된 부분(Figma 그라데이션)과 커서를 오버레이한다.
function TypedLine({
  text,
  revealed,
  caret,
}: {
  text: string;
  revealed: number;
  caret: boolean;
}) {
  return (
    <span className="block">
      <span className="relative inline-block whitespace-nowrap align-top">
        <span className="text-transparent">{text}</span>
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 whitespace-pre"
        >
          <span className="bg-[linear-gradient(92deg,#040435_1.85%,#103AA3_97.41%)] bg-clip-text text-transparent">
            {text.slice(0, revealed)}
          </span>
          {caret && <Caret />}
        </span>
      </span>
    </span>
  );
}

// hero 제목 타이핑 애니메이션 — 각 줄을 한 글자씩 순차 노출하고, 타이핑 중인 줄에 커서를 둔다(뷰포트 1회).
export function HeroTypingTitle({
  lines,
  className,
  startDelayMs = 0,
}: {
  lines: string[];
  className?: string;
  startDelayMs?: number;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduceMotion = !!useReducedMotion();

  const totalChars = lines.reduce((sum, line) => sum + line.length, 0);
  const count = useTypewriter(totalChars, {
    active: inView,
    instant: reduceMotion,
    startDelayMs,
  });

  const done = count >= totalChars;
  const offsets = cumulativeOffsets(lines);

  return (
    <h1 ref={ref} className={className}>
      {lines.map((line, i) => {
        const start = offsets[i];
        const revealed = Math.min(Math.max(count - start, 0), line.length);
        // 현재 타이핑 중인 줄에만 커서 표시
        const typingThisLine =
          !done && count >= start && count <= start + line.length;
        return (
          <TypedLine
            key={i}
            text={line}
            revealed={revealed}
            caret={!reduceMotion && typingThisLine}
          />
        );
      })}
    </h1>
  );
}
